import { PREDEFINED_TASKS, type PredefinedTask } from "@/components/ai/ai-constants";
import type {
    FromScratchContext,
    GitCommitContext,
    GitTagContext,
    IssueContext,
    PromptInputSubmission,
    PullRequestContext,
    RepoContext,
    SelectedContext,
} from "@/components/ai/prompt-input/NewPromptInput";
import type { EnvironmentClassEntry, EnvironmentClassMap } from "@/hooks/use-environment-class-entries";
import type { PlainProject } from "@/queries/project-queries";
import { getProjectQuery } from "@/queries/project-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { clearPendingSubmissionFromLocalStorage } from "@/routes/ona-onboarding/local-storage";
import type { GitpodAPI } from "@/api";
import { atom, useAtom, useSetAtom, useStore } from "jotai";
import { atomEffect } from "jotai-effect";
import { atomFamily, atomWithLazy } from "jotai/utils";
import type { QueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

// ============================================================================
// Unified Prompt Input State Storage
// ============================================================================

/**
 * Stored version of ProjectContext with only IDs for re-resolution on load.
 */
export type StoredProjectContext = {
    kind: "project";
    projectId: string;
    projectName: string;
    environmentClassIds: string[];
};

/**
 * Union type for contexts that can be stored in localStorage.
 * Most contexts are stored as-is, but ProjectContext is reduced to IDs only.
 */
export type StoredContext =
    | PullRequestContext
    | IssueContext
    | RepoContext
    | GitTagContext
    | GitCommitContext
    | FromScratchContext
    | StoredProjectContext;

/**
 * Stored environment class with minimal fields for display before resolution.
 */
export type StoredEnvironmentClass = {
    id: string;
    displayName: string;
};

/**
 * Unified state stored in localStorage for the prompt input.
 * Contains prompt text, contexts, and environment class selection.
 */
export type StoredPromptInputState = {
    prompt: string;
    contexts: StoredContext[];
    environmentClass: StoredEnvironmentClass | null;
};

/**
 * Converts a SelectedContext to a StoredContext for localStorage persistence.
 */
export function toStoredContext(context: SelectedContext): StoredContext {
    if (context.kind === "project") {
        return {
            kind: "project",
            projectId: context.project.id,
            projectName: context.project.metadata?.name ?? "",
            environmentClassIds: context.environmentClasses.map((ec) => ec.clazz.id),
        };
    }
    return context;
}

/**
 * Converts a StoredContext back to a SelectedContext.
 * For StoredProjectContext, requires resolved project data.
 */
export function fromStoredContext(
    stored: StoredContext,
    resolvedData?: {
        project?: PlainProject;
        environmentClasses?: EnvironmentClassEntry[];
    },
): SelectedContext | null {
    if (stored.kind === "project") {
        if (resolvedData?.project) {
            return {
                kind: "project",
                project: resolvedData.project,
                environmentClasses: resolvedData.environmentClasses ?? [],
            };
        }
        return null;
    }
    return stored;
}

/**
 * Type guard to check if a StoredContext is a StoredProjectContext
 */
export function isStoredProjectContext(context: StoredContext): context is StoredProjectContext {
    return context.kind === "project" && "projectId" in context;
}

/**
 * Unresolved project context - displayed while the full project data is being fetched.
 * Has the same shape as ProjectContext but with a flag to indicate it's not yet resolved.
 */
export type UnresolvedProjectContext = {
    kind: "project-unresolved";
    projectId: string;
    projectName: string;
    environmentClassIds: string[];
};

/**
 * Context that can be displayed in the UI. Includes both resolved and unresolved variants.
 */
export type DisplayableContext = SelectedContext | UnresolvedProjectContext;

/**
 * Type guard to check if a context is unresolved
 */
export function isUnresolvedContext(context: DisplayableContext): context is UnresolvedProjectContext {
    return context.kind === "project-unresolved";
}

/**
 * Converts a StoredProjectContext to an UnresolvedProjectContext for immediate display.
 */
export function toUnresolvedProjectContext(stored: StoredProjectContext): UnresolvedProjectContext {
    return {
        kind: "project-unresolved",
        projectId: stored.projectId,
        projectName: stored.projectName,
        environmentClassIds: stored.environmentClassIds,
    };
}

// localStorage key
const PROMPT_INPUT_STATE_KEY = "prompt-box-state-home";
// Legacy key used before unified storage was introduced
const LEGACY_PROMPT_KEY = "conversation-input-home";

/**
 * Creates an empty stored state.
 */
function createEmptyStoredState(): StoredPromptInputState {
    return { prompt: "", contexts: [], environmentClass: null };
}

/**
 * Removes legacy localStorage key if it exists.
 */
function cleanupLegacyStorage(organizationId: string): void {
    const legacyKey = `${LEGACY_PROMPT_KEY}_${organizationId}`;
    localStorage.removeItem(legacyKey);
}

/**
 * Loads stored prompt input state from localStorage.
 * Also cleans up any legacy storage keys.
 */
export function loadStoredPromptInputState(organizationId: string): StoredPromptInputState {
    const key = `${PROMPT_INPUT_STATE_KEY}_${organizationId}`;
    const stored = localStorage.getItem(key);

    // Always clean up legacy storage
    cleanupLegacyStorage(organizationId);

    if (stored) {
        try {
            return JSON.parse(stored) as StoredPromptInputState;
        } catch {
            localStorage.removeItem(key);
        }
    }

    return createEmptyStoredState();
}

/**
 * Saves prompt input state to localStorage.
 */
export function saveStoredPromptInputState(organizationId: string, state: StoredPromptInputState): void {
    const key = `${PROMPT_INPUT_STATE_KEY}_${organizationId}`;
    const isEmpty = !state.prompt && state.contexts.length === 0 && !state.environmentClass;

    if (isEmpty) {
        localStorage.removeItem(key);
    } else {
        try {
            localStorage.setItem(key, JSON.stringify(state));
        } catch {
            console.warn("Failed to save prompt input state to localStorage - quota may be exceeded");
        }
    }
}

/**
 * Clears stored prompt input state from localStorage.
 */
export function clearStoredPromptInputState(organizationId: string): void {
    localStorage.removeItem(`${PROMPT_INPUT_STATE_KEY}_${organizationId}`);
}

/**
 * Atom to track whether stored state is being resolved (project/env class data being fetched).
 */
export const isResolvingStoredStateAtom = atom(false);

/**
 * Atom to track whether initial restore from localStorage has completed.
 * Auto-save should not run until this is true to avoid overwriting stored data.
 */
export const hasCompletedInitialRestoreAtom = atom(false);

/**
 * Atom to hold unresolved project contexts for display while resolution is in progress.
 * These are shown as chips with a loading indicator.
 */
export const unresolvedProjectContextsAtom = atom<UnresolvedProjectContext[]>([]);

/**
 * Atom to hold the unresolved environment class for display while resolution is in progress.
 */
export const unresolvedEnvironmentClassAtom = atom<StoredEnvironmentClass | null>(null);

export const DROPDOWN_MENU_KIND = {
    none: "none",
    context: "context",
    contextAssociatedProjectSelector: "contextAssociatedProjectSelector",
    projectSelector: "projectSelector",
    urlSelector: "repoSelector",
    environmentClassSelector: "environmentClassSelector",
    /**
     * @deprecated this was used in the old onboarding flow to display the repository browser, but this is no longer used and should be removed.
     */
    repositoryBrowser: "repositoryBrowser",
} as const;
export type DropdownMenuKind = (typeof DROPDOWN_MENU_KIND)[keyof typeof DROPDOWN_MENU_KIND];

export const promptTextareaHeightAtom = atom(0);

export const selectedTasksAtom = atom<PredefinedTask[]>([...PREDEFINED_TASKS]);

export const dropdownMenuKindAtom = atom<DropdownMenuKind>(DROPDOWN_MENU_KIND.none);
export const dropdownMenuWidthAtom = atom(0);

// stores all projects found for a specific context url
export const contextAssociatedProjectsAtom = atom<PlainProject[]>([]);
export const selectedContextsAtom = atom<SelectedContext[]>([]);
export const projectContextAtom = atom((get) =>
    get(selectedContextsAtom).find((context) => context.kind === "project"),
);
export const selectedEnvironmentClassAtom = atom<EnvironmentClassEntry | null>(null);
export const selectedDevRunnerIdAtom = atom<string | null>(null);
export const isAwaitingGitHubAuthAtom = atom(false);

// Image attachments for agent messages
export type ImageAttachment = {
    id: string;
    data: Uint8Array;
    preview: string;
    name: string;
    mimeType: string;
};
export const selectedImagesAtom = atom<ImageAttachment[]>([]);

export const isLoadingAtom = atom(false);

/**
 * Atom family for prompt text, keyed by organizationId.
 * Initializes from unified storage (with legacy migration fallback).
 */
export const promptAtomFamily = atomFamily((organizationId: string) =>
    atomWithLazy(() => {
        if (typeof window !== "undefined") {
            const state = loadStoredPromptInputState(organizationId);
            return state.prompt;
        }
        return "";
    }),
);

/**
 * Returns the prompt atom scoped to the current user's organization.
 * When prompt is set to empty string, also clears localStorage.
 */
export const usePromptAtom = () => {
    const { data: user } = useAuthenticatedUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const organizationId = user.organizationId;
    const _promptAtom = promptAtomFamily(organizationId);

    return useMemo(
        () =>
            atom(
                (get) => get(_promptAtom),
                (_get, set, value: string) => {
                    set(_promptAtom, value);
                    if (!value) {
                        // When prompt is cleared, clear localStorage to prevent stale data
                        clearStoredPromptInputState(organizationId);
                    }
                },
            ),
        [_promptAtom, organizationId],
    );
};

/**
 * Hook that returns a function to clear all prompt box state (prompt, contexts, environment class)
 * and localStorage in one atomic operation.
 */
export const useClearPromptBoxState = () => {
    const { data: user } = useAuthenticatedUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const organizationId = user.organizationId;
    const setPrompt = useSetAtom(promptAtomFamily(organizationId));
    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setSelectedImages = useSetAtom(selectedImagesAtom);
    const store = useStore();

    return useCallback(() => {
        setSelectedContexts([]);
        setSelectedEnvironmentClass(null);
        setPrompt("");
        // Revoke blob URLs to free memory before clearing images
        const images = store.get(selectedImagesAtom);
        for (const image of images) {
            URL.revokeObjectURL(image.preview);
        }
        setSelectedImages([]);
        clearStoredPromptInputState(organizationId);
    }, [setPrompt, setSelectedContexts, setSelectedEnvironmentClass, setSelectedImages, store, organizationId]);
};

/**
 * Hook to auto-save all prompt input state (prompt, contexts, environment class) to localStorage.
 * Uses a single unified storage entry per organization.
 */
export const useAutoSavePromptInputState = () => {
    const { data: user } = useAuthenticatedUser();
    if (!user) {
        throw new Error("User not authenticated");
    }

    const organizationId = user.organizationId;
    const _promptAtom = promptAtomFamily(organizationId);

    // Create a debounced save function
    const debouncedSave = useDebouncedCallback((state: StoredPromptInputState) => {
        saveStoredPromptInputState(organizationId, state);
    }, 500);

    // Cancel any pending saves on unmount to prevent stale data from being saved
    useEffect(() => {
        return () => {
            debouncedSave.cancel();
        };
    }, [debouncedSave]);

    // The effect function reads atoms and triggers debounced save
    // Only saves after initial restore has completed to avoid overwriting stored data
    const effectFn: Parameters<typeof atomEffect>[0] = useCallback(
        (get) => {
            const hasCompletedInitialRestore = get(hasCompletedInitialRestoreAtom);
            if (!hasCompletedInitialRestore) {
                return;
            }

            const prompt = get(_promptAtom);
            const contexts = get(selectedContextsAtom);
            const environmentClass = get(selectedEnvironmentClassAtom);

            const state: StoredPromptInputState = {
                prompt,
                contexts: contexts.map(toStoredContext),
                environmentClass: environmentClass
                    ? { id: environmentClass.clazz.id, displayName: environmentClass.clazz.displayName }
                    : null,
            };

            const isEmpty = !state.prompt && state.contexts.length === 0 && !state.environmentClass;
            if (isEmpty) {
                // When state is empty, cancel any pending saves and clear immediately
                debouncedSave.cancel();
                clearStoredPromptInputState(organizationId);
            } else {
                debouncedSave(state);
            }
        },
        [_promptAtom, debouncedSave, organizationId],
    );

    const autoSaveEffect = useMemo(() => atomEffect(effectFn), [effectFn]);

    useAtom(autoSaveEffect);
};

/**
 * @deprecated Use useAutoSavePromptInputState instead. Kept for backward compatibility.
 */
export const useAutoSavePrompt = useAutoSavePromptInputState;

/**
 * Options for useRestoreStoredPromptInputState hook.
 */
export type RestoreStoredStateOptions = {
    api: GitpodAPI;
    queryClient: QueryClient;
    environmentClassMap: EnvironmentClassMap;
    isEnvironmentClassMapLoading: boolean;
    onFailedProjects?: (failedProjects: string[]) => void;
    onFailedEnvironmentClass?: (environmentClassName: string) => void;
};

/**
 * Hook to restore stored prompt input state from localStorage on mount.
 * Resolves project contexts by fetching project data and environment classes.
 */
export const useRestoreStoredPromptInputState = ({
    api,
    queryClient,
    environmentClassMap,
    isEnvironmentClassMapLoading,
    onFailedProjects,
    onFailedEnvironmentClass,
}: RestoreStoredStateOptions) => {
    const { data: user } = useAuthenticatedUser();
    const organizationId = user?.organizationId;

    const setSelectedContexts = useSetAtom(selectedContextsAtom);
    const setSelectedEnvironmentClass = useSetAtom(selectedEnvironmentClassAtom);
    const setIsResolvingStoredState = useSetAtom(isResolvingStoredStateAtom);
    const setHasCompletedInitialRestore = useSetAtom(hasCompletedInitialRestoreAtom);
    const setUnresolvedProjectContexts = useSetAtom(unresolvedProjectContextsAtom);
    const setUnresolvedEnvironmentClass = useSetAtom(unresolvedEnvironmentClassAtom);

    const hasRestoredStoredState = useRef(false);
    const hasShownUnresolvedState = useRef(false);
    const previousOrgId = useRef<string | undefined>(undefined);

    // Reset restoration flags when organization changes
    useEffect(() => {
        if (organizationId !== previousOrgId.current) {
            hasRestoredStoredState.current = false;
            hasShownUnresolvedState.current = false;
            previousOrgId.current = organizationId;
        }
    }, [organizationId]);

    // Show unresolved state immediately (before env class map is loaded)
    useEffect(() => {
        if (!organizationId || hasShownUnresolvedState.current) {
            return;
        }

        const storedState = loadStoredPromptInputState(organizationId);
        const hasStoredContexts = storedState.contexts.length > 0;
        const hasStoredEnvClass = !!storedState.environmentClass;

        if (!hasStoredContexts && !hasStoredEnvClass) {
            hasShownUnresolvedState.current = true;
            hasRestoredStoredState.current = true;
            setHasCompletedInitialRestore(true);
            return;
        }

        // Check if any stored contexts are project contexts that need resolution
        const projectContexts = storedState.contexts.filter(isStoredProjectContext);
        const nonProjectContexts = storedState.contexts.filter((c) => !isStoredProjectContext(c)) as SelectedContext[];

        // Set non-project contexts immediately
        if (nonProjectContexts.length > 0) {
            setSelectedContexts(nonProjectContexts);
        }

        // Show unresolved environment class immediately
        if (hasStoredEnvClass) {
            setUnresolvedEnvironmentClass(storedState.environmentClass);
        }

        // Show unresolved project contexts immediately
        if (projectContexts.length > 0) {
            const unresolvedContexts = projectContexts.map(toUnresolvedProjectContext);
            setUnresolvedProjectContexts(unresolvedContexts);
            setIsResolvingStoredState(true);
        }

        hasShownUnresolvedState.current = true;
    }, [
        organizationId,
        setSelectedContexts,
        setUnresolvedEnvironmentClass,
        setUnresolvedProjectContexts,
        setIsResolvingStoredState,
        setHasCompletedInitialRestore,
    ]);

    // Resolve to full state once env class map is loaded
    useEffect(() => {
        if (
            !organizationId ||
            hasRestoredStoredState.current ||
            isEnvironmentClassMapLoading ||
            !hasShownUnresolvedState.current
        ) {
            return;
        }

        const storedState = loadStoredPromptInputState(organizationId);
        const hasStoredEnvClass = !!storedState.environmentClass;
        const projectContexts = storedState.contexts.filter(isStoredProjectContext);
        const nonProjectContexts = storedState.contexts.filter((c) => !isStoredProjectContext(c)) as SelectedContext[];

        // Resolve environment class now that map is loaded
        let resolvedEnvClass: EnvironmentClassEntry | null = null;
        if (hasStoredEnvClass && environmentClassMap[storedState.environmentClass!.id]) {
            resolvedEnvClass = environmentClassMap[storedState.environmentClass!.id];
            setSelectedEnvironmentClass(resolvedEnvClass);
            setUnresolvedEnvironmentClass(null);
        } else if (hasStoredEnvClass) {
            // Env class not found in map - clear unresolved state and notify
            setUnresolvedEnvironmentClass(null);
            onFailedEnvironmentClass?.(storedState.environmentClass!.displayName);
        }

        if (projectContexts.length === 0) {
            // No project contexts to resolve
            hasRestoredStoredState.current = true;
            setHasCompletedInitialRestore(true);
            return;
        }

        // Resolve project contexts asynchronously
        const resolveProjectContexts = async () => {
            const resolvedContexts: SelectedContext[] = [...nonProjectContexts];
            const failedProjects: string[] = [];

            for (const storedProject of projectContexts) {
                try {
                    const projectQuery = getProjectQuery(api, storedProject.projectId);
                    const project = await queryClient.fetchQuery(projectQuery);

                    // Resolve environment classes from the stored IDs
                    const resolvedEnvClasses = storedProject.environmentClassIds
                        .map((id) => environmentClassMap[id])
                        .filter(Boolean);

                    resolvedContexts.push({
                        kind: "project",
                        project,
                        environmentClasses: resolvedEnvClasses,
                    });
                } catch (error) {
                    console.warn(`Failed to resolve stored project context: ${storedProject.projectId}`, error);
                    failedProjects.push(storedProject.projectName || storedProject.projectId);
                }
            }

            setSelectedContexts(resolvedContexts);
            setUnresolvedProjectContexts([]); // Clear unresolved contexts
            setUnresolvedEnvironmentClass(null); // Clear unresolved env class
            setIsResolvingStoredState(false);
            hasRestoredStoredState.current = true;
            setHasCompletedInitialRestore(true);

            // Notify about failed projects and save successfully resolved state
            if (failedProjects.length > 0) {
                onFailedProjects?.(failedProjects);
            }

            // Save the successfully resolved state (without failed projects/env class)
            const hasFailures = failedProjects.length > 0 || (hasStoredEnvClass && !resolvedEnvClass);
            if (hasFailures) {
                saveStoredPromptInputState(organizationId, {
                    prompt: storedState.prompt,
                    contexts: resolvedContexts.map(toStoredContext),
                    environmentClass: resolvedEnvClass
                        ? { id: resolvedEnvClass.clazz.id, displayName: resolvedEnvClass.clazz.displayName }
                        : null,
                });
            }
        };

        void resolveProjectContexts();
    }, [
        organizationId,
        environmentClassMap,
        isEnvironmentClassMapLoading,
        api,
        queryClient,
        setSelectedContexts,
        setSelectedEnvironmentClass,
        setIsResolvingStoredState,
        setHasCompletedInitialRestore,
        setUnresolvedProjectContexts,
        setUnresolvedEnvironmentClass,
        onFailedProjects,
        onFailedEnvironmentClass,
    ]);
};

export const pendingSubmissionAtomFamily = atomFamily((promptAtom: ReturnType<typeof usePromptAtom>) => {
    const a = atom(null, (_get, set, pendingSubmission: PromptInputSubmission | null) => {
        set(a, pendingSubmission);
        if (pendingSubmission) {
            set(promptAtom, pendingSubmission.prompts[0]);
            set(selectedContextsAtom, pendingSubmission.contexts);
            set(selectedEnvironmentClassAtom, pendingSubmission.environmentClass);
        } else {
            set(promptAtom, "");
            set(selectedContextsAtom, []);
            set(selectedEnvironmentClassAtom, null);
        }
    });
    return a;
});

export const usePendingSubmission = () => {
    const { data: user } = useAuthenticatedUser();
    const promptAtom = usePromptAtom();
    const clearPendingSubmission = useCallback(() => {
        if (user?.organizationId) {
            clearPendingSubmissionFromLocalStorage(user.organizationId);
        }
        pendingSubmissionAtomFamily.remove(promptAtom);
    }, [user, promptAtom]);
    return {
        pendingSubmissionAtom: pendingSubmissionAtomFamily(promptAtom),
        clearPendingSubmission,
    };
};
